import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Document } from '@/hooks/useDocuments';
import type { AttachmentSelection, BackendDocumentSelection, DocumentSelection } from '@/types';

interface UseDocumentSelectionsProps {
  documents: Document[] | undefined;
  routerQueryDocuments: string | string[] | undefined;
  /** Optional JSON BackendDocumentSelection[] from homepage handoff. */
  routerQueryDocSel?: string | undefined;
  /** True while SWR is fetching/revalidating the document list used for URL hydration. */
  isDocumentsValidating?: boolean;
}

export type UrlDocumentHydrationStatus = 'idle' | 'pending' | 'ready';

interface UseDocumentSelectionsReturn {
  documentSelections: Map<string, DocumentSelection>;
  setDocumentSelections: React.Dispatch<React.SetStateAction<Map<string, DocumentSelection>>>;
  /** Whether URL `documents=` selections have finished hydrating (or there were none). */
  urlDocumentHydrationStatus: UrlDocumentHydrationStatus;
  toggleDocumentSelection: (doc: Document) => void;
  toggleParentInclusion: (docGuid: string) => void;
  toggleAttachmentInclusion: (parentGuid: string, attachmentGuid: string) => void;
  removeDocumentGroup: (docGuid: string) => void;
  getDocumentSelectionsForBackend: () => BackendDocumentSelection[];
  getDocumentAttachments: (docGuid: string) => AttachmentSelection[];
  clearSelections: () => void;
}

/**
 * Creates a DocumentSelection with embedded metadata from a document
 */
export function createDocumentSelection(doc: Document): DocumentSelection {
  // Attachments are Documents, use Document properties
  const attachments: AttachmentSelection[] = (doc.attachments || []).map((att: Document) => ({
    guid: att.guid,
    included: true,
    filename: att.fileName || '',
    token_count: att.token_count || 0,
    hasAttachments: Array.isArray(att.attachments) && att.attachments.length > 0,
  }));

  return {
    guid: doc.guid,
    include_parent: true,
    attachments,
    displayName: doc.displayName || doc.fileName || '',
    fileName: doc.fileName || '',
    token_count: doc.token_count || 0,
  };
}

type StoredAttachmentSelection = string | { guid: string };

function attachmentIsIncluded(
  stored: StoredAttachmentSelection[] | undefined,
  guid: string,
): boolean {
  if (!Array.isArray(stored)) return true;
  return stored.some((item) => (
    typeof item === 'string' ? item === guid : item.guid === guid
  ));
}

export function buildLoadedChatSelections(
  loadedSelections: DocumentSelection[],
  documents: Document[] | undefined,
): Map<string, DocumentSelection> {
  const selections = new Map<string, DocumentSelection>();
  if (!documents?.length) {
    return selections;
  }

  for (const sel of loadedSelections) {
    const doc = documents.find((item) => item.guid === sel.guid);
    if (!doc) continue;

    const freshSelection = createDocumentSelection(doc);
    const mergedSelection: DocumentSelection = {
      ...freshSelection,
      include_parent: sel.include_parent ?? true,
      attachments: freshSelection.attachments.map((att) => ({
        ...att,
        included: attachmentIsIncluded(
          sel.attachments as StoredAttachmentSelection[] | undefined,
          att.guid,
        ),
      })),
    };

    selections.set(sel.guid, mergedSelection);
  }

  return selections;
}

/** Apply homepage inclusion overrides onto a freshly created selection. */
export function applyDocSelOverride(
  selection: DocumentSelection,
  override: BackendDocumentSelection | undefined,
): DocumentSelection {
  if (!override) return selection;
  const includedAttachmentIds = new Set(override.attachments);
  return {
    ...selection,
    include_parent: override.include_parent,
    attachments: selection.attachments.map((attachment) => ({
      ...attachment,
      included: includedAttachmentIds.has(attachment.guid),
    })),
  };
}

function parseDocSelOverrides(raw: string | undefined): Map<string, BackendDocumentSelection> {
  if (!raw) return new Map();
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Map();
    const overrides = new Map<string, BackendDocumentSelection>();
    for (const item of parsed) {
      if (
        !item ||
        typeof item !== 'object' ||
        typeof (item as BackendDocumentSelection).guid !== 'string' ||
        typeof (item as BackendDocumentSelection).include_parent !== 'boolean' ||
        !Array.isArray((item as BackendDocumentSelection).attachments)
      ) {
        continue;
      }
      const selection = item as BackendDocumentSelection;
      overrides.set(selection.guid, {
        guid: selection.guid,
        include_parent: selection.include_parent,
        attachments: selection.attachments.filter((id): id is string => typeof id === 'string'),
      });
    }
    return overrides;
  } catch {
    return new Map();
  }
}

/**
 * Custom hook for managing document selections in Case Partner
 */
export function useDocumentSelections({
  documents,
  routerQueryDocuments,
  routerQueryDocSel,
  isDocumentsValidating = false,
}: UseDocumentSelectionsProps): UseDocumentSelectionsReturn {
  // Track all document selections with embedded metadata
  const [documentSelections, setDocumentSelectionsState] = useState<Map<string, DocumentSelection>>(new Map());
  const documentSelectionsRef = useRef(documentSelections);
  // Pending document IDs from URL that need to be applied once documents load
  const [pendingUrlDocumentIds, setPendingUrlDocumentIds] = useState<string[] | null>(null);
  // Query key whose URL selections have finished applying (or confirmed empty).
  const [hydratedUrlDocumentsKey, setHydratedUrlDocumentsKey] = useState<string | null>(null);

  const setDocumentSelections = useCallback<React.Dispatch<React.SetStateAction<Map<string, DocumentSelection>>>>((next) => {
    const nextSelections = typeof next === 'function'
      ? next(documentSelectionsRef.current)
      : next;

    documentSelectionsRef.current = nextSelections;
    setDocumentSelectionsState(nextSelections);
  }, []);

  // Get attachments for a document from selection metadata
  const getDocumentAttachments = useCallback((docGuid: string): AttachmentSelection[] => {
    const selection = documentSelections.get(docGuid);
    return selection?.attachments || [];
  }, [documentSelections]);

  // Toggle adding/removing a document group
  const toggleDocumentSelection = useCallback((doc: Document) => {
    setDocumentSelections(prev => {
      const newMap = new Map(prev);

      // If document is already selected, remove it
      if (newMap.has(doc.guid)) {
        newMap.delete(doc.guid);
        return newMap;
      }

      // Add the document with embedded metadata
      newMap.set(doc.guid, createDocumentSelection(doc));
      return newMap;
    });
  }, []);

  // Toggle parent document inclusion
  const toggleParentInclusion = useCallback((docGuid: string) => {
    setDocumentSelections(prev => {
      const newMap = new Map(prev);
      const selection = newMap.get(docGuid);
      if (!selection) return prev;

      newMap.set(docGuid, { ...selection, include_parent: !selection.include_parent });
      return newMap;
    });
  }, []);

  // Toggle attachment inclusion
  const toggleAttachmentInclusion = useCallback((parentGuid: string, attachmentGuid: string) => {
    setDocumentSelections(prev => {
      const newMap = new Map(prev);
      const selection = newMap.get(parentGuid);
      if (!selection) return prev;

      const newAttachments = selection.attachments.map(att =>
        att.guid === attachmentGuid ? { ...att, included: !att.included } : att
      );

      newMap.set(parentGuid, { ...selection, attachments: newAttachments });
      return newMap;
    });
  }, []);

  // Remove a document group entirely
  const removeDocumentGroup = useCallback((docGuid: string) => {
    setDocumentSelections(prev => {
      const newMap = new Map(prev);
      newMap.delete(docGuid);
      return newMap;
    });
  }, []);

  // Get selections formatted for backend API
  // Backend expects attachments as List[str] (GUIDs only), not full AttachmentSelection objects
  const getDocumentSelectionsForBackend = useCallback((): BackendDocumentSelection[] => {
    const selections: BackendDocumentSelection[] = [];

    for (const [, selection] of documentSelectionsRef.current) {
      // Only add if parent is included OR there are attachments to include
      const includedAttachmentGuids = selection.attachments
        .filter(att => att.included)
        .map(att => att.guid);

      if (selection.include_parent || includedAttachmentGuids.length > 0) {
        selections.push({
          guid: selection.guid,
          include_parent: selection.include_parent,
          attachments: includedAttachmentGuids,
        });
      }
    }

    return selections;
  }, []);

  // Clear all selections
  const clearSelections = useCallback(() => {
    setDocumentSelections(new Map());
    setPendingUrlDocumentIds(null);
    setHydratedUrlDocumentsKey(null);
  }, []);

  const urlDocumentsKey = useMemo(() => {
    if (!routerQueryDocuments) return null;
    const documentIds =
      typeof routerQueryDocuments === 'string'
        ? routerQueryDocuments.split(',')
        : routerQueryDocuments;
    const validIds = documentIds
      .filter((id) => typeof id === 'string' && id.trim().length > 0)
      .map((id) => id.trim());
    if (validIds.length === 0) return null;
    return validIds.join(',');
  }, [routerQueryDocuments]);

  // When URL changes, queue the document IDs for selection
  useEffect(() => {
    if (!urlDocumentsKey) {
      setPendingUrlDocumentIds(null);
      setHydratedUrlDocumentsKey(null);
      return;
    }

    setHydratedUrlDocumentsKey(null);
    setPendingUrlDocumentIds(urlDocumentsKey.split(','));
  }, [urlDocumentsKey]);

  // When we have pending IDs and documents are loaded, apply the selections.
  // Ignore a stale pending set from the previous URL. Both effects run in one
  // commit when `documents=` changes, and applying the old ids would mark the
  // new key ready for auto-send.
  useEffect(() => {
    if (!pendingUrlDocumentIds || documents === undefined || !urlDocumentsKey) {
      return;
    }
    if (pendingUrlDocumentIds.join(',') !== urlDocumentsKey) {
      return;
    }

    // Filter to only IDs that exist in the documents list
    const validIds = pendingUrlDocumentIds.filter((id) => documents.some((d) => d.guid === id));
    const missingRequestedIds = validIds.length !== pendingUrlDocumentIds.length;

    // Stale SWR cache during revalidation can omit a just-uploaded document.
    // Keep the IDs pending until that fetch settles so auto-select can still run.
    if (missingRequestedIds && isDocumentsValidating) {
      return;
    }

    if (validIds.length > 0) {
      const newSelectionsMap = new Map<string, DocumentSelection>();

      const docSelOverrides = parseDocSelOverrides(routerQueryDocSel);
      for (const docGuid of validIds) {
        const doc = documents.find((d) => d.guid === docGuid);
        if (doc) {
          const selection = applyDocSelOverride(
            createDocumentSelection(doc),
            docSelOverrides.get(docGuid),
          );
          newSelectionsMap.set(docGuid, selection);
        }
      }

      setDocumentSelections(newSelectionsMap);
    }

    // Documents have loaded (even if some URL ids were missing). Clear pending so
    // auto-send can proceed; unmatched ids simply stay unselected.
    setPendingUrlDocumentIds(null);
    setHydratedUrlDocumentsKey(urlDocumentsKey);
  }, [pendingUrlDocumentIds, documents, urlDocumentsKey, routerQueryDocSel, isDocumentsValidating]);

  const urlDocumentHydrationStatus: UrlDocumentHydrationStatus = useMemo(() => {
    if (!urlDocumentsKey) return 'idle';
    if (hydratedUrlDocumentsKey === urlDocumentsKey) return 'ready';
    return 'pending';
  }, [urlDocumentsKey, hydratedUrlDocumentsKey]);

  return {
    documentSelections,
    setDocumentSelections,
    urlDocumentHydrationStatus,
    toggleDocumentSelection,
    toggleParentInclusion,
    toggleAttachmentInclusion,
    removeDocumentGroup,
    getDocumentSelectionsForBackend,
    getDocumentAttachments,
    clearSelections,
  };
}
